import { useCallback, useContext, useMemo } from 'react'
import { clsx } from 'clsx'
import type { FieldBlockProps } from '../../FieldBlock'
import DataContext from '../../DataContext/Context'
import type { FieldStringProps as StringFieldProps } from '../String'
import StringField from '../String'
import CompositionField from '../Composition'
import type { CountryCode } from '../../types'
import useTranslation from '../../hooks/useTranslation'
import useDataValue from '../../hooks/useDataValue'
import { isPath } from '../../utils/json-pointer'
import { COUNTRY as defaultCountry } from '../../../../shared/defaults'
import type { SpacingProps } from '../../../../shared/types'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type FieldPostalCodeAndCityProps = Pick<
  FieldBlockProps,
  | 'error'
  | 'warning'
  | 'info'
  | 'width'
  | 'className'
  | 'help'
  | keyof SpacingProps
> &
  Partial<Record<'postalCode' | 'city', StringFieldProps>> & {
    /**
     * Defines which country the postal code and city is for.
     * Setting it to anything other than `no` will remove the default norwegian postal code pattern.
     * You can also use the value of another field to define the countryCode, by using a path value i.e. `/myCountryCodePath`.
     * Default: `NO`
     */
    countryCode?: CountryCode
  } & Pick<StringFieldProps, 'size'>

function PostalCodeAndCity(props: FieldPostalCodeAndCityProps) {
  const translations = useTranslation()
  const countryCodeFromProvider = useContext(DataContext)?.countryCode

  const {
    postalCode = {},
    city = {},
    help,
    width = 'large',
    countryCode = countryCodeFromProvider ?? defaultCountry,
    size,
    ...compositionFieldProps
  } = props

  const countryCodePath = isPath(countryCode) ? countryCode : undefined
  const { value: countryCodePathValue, getSourceValue } =
    useDataValue(countryCodePath)
  const countryCodeValue = countryCodePath
    ? countryCodePathValue
    : getSourceValue(countryCode)

  const handleCityDefaults = useCallback(
    (city: StringFieldProps) => {
      const props: StringFieldProps = {}

      switch (countryCodeValue) {
        case defaultCountry: {
          props.pattern = '^[A-Za-zÆØÅæøå -]+$'
          break
        }
      }

      return { ...props, ...city }
    },
    [countryCodeValue]
  )

  const {
    pattern: cityPattern,
    className: cityClassName,
    label: cityLabel,
    width: cityWidth,
    errorMessages: cityErrorMessages,
  } = handleCityDefaults(city)

  const handlePostalCodeDefaults = useCallback(
    (postalCode: StringFieldProps) => {
      const props: StringFieldProps = {}

      switch (countryCodeValue) {
        case defaultCountry:
        case 'DK':
        case 'CH': {
          props.mask = [/\d/, /\d/, /\d/, /\d/]
          props.pattern = '^[0-9]{4}$'
          break
        }
        default:
          props.width = '8rem'
          break
      }

      return { ...props, ...postalCode }
    },
    [countryCodeValue]
  )

  const {
    mask: postalCodeMask,
    pattern: postalCodePattern,
    placeholder: postalCodePlaceHolder,
    className: postalCodeClassName,
    label: postalCodeLabel,
    width: postalCodeWidth,
    errorMessages: postalCodeErrorMessages,
  } = handlePostalCodeDefaults(postalCode)

  return (
    <CompositionField
      className={clsx(
        'dnb-forms-field-postal-code-and-city',
        props.className
      )}
      {...compositionFieldProps}
      width={width}
    >
      <StringField
        size={size}
        className={clsx(
          'dnb-forms-field-postal-code-and-city__postal-code',
          postalCodeClassName
        )}
        label={postalCodeLabel ?? translations.PostalCode.label}
        mask={postalCodeMask}
        allowOverflow
        pattern={postalCodePattern}
        placeholder={postalCodePlaceHolder}
        errorMessages={useMemo(
          () => ({
            'Field.errorRequired': translations.PostalCode.errorRequired,
            'Field.errorPattern': translations.PostalCode.errorPattern,
            ...postalCodeErrorMessages,
          }),
          [
            postalCodeErrorMessages,
            translations.PostalCode.errorPattern,
            translations.PostalCode.errorRequired,
          ]
        )}
        width={postalCodeWidth ?? false}
        inputClassName="dnb-forms-field-postal-code-and-city__postal-code-input"
        inputMode="numeric"
        autoComplete="postal-code"
        data-country-code={countryCode}
        {...postalCode}
      />

      <StringField
        help={help}
        size={size}
        className={clsx(
          'dnb-forms-field-postal-code-and-city__city',
          cityClassName
        )}
        label={cityLabel ?? translations.City.label}
        errorMessages={useMemo(
          () => ({
            'Field.errorRequired': translations.City.errorRequired,
            'Field.errorPattern': translations.City.errorPattern,
            ...cityErrorMessages,
          }),
          [
            cityErrorMessages,
            translations.City.errorPattern,
            translations.City.errorRequired,
          ]
        )}
        pattern={cityPattern}
        trim
        width={cityWidth ?? 'stretch'}
        inputClassName="dnb-forms-field-postal-code-and-city__city-input"
        autoComplete="address-level2"
        {...city}
      />
    </CompositionField>
  )
}

withComponentMarkers(PostalCodeAndCity, {
  _supportsSpacingProps: undefined,
})

export default PostalCodeAndCity
